/**
 * The public front door: what Legal CRM is, for somebody who has not signed in.
 *
 * Shown at `/` to a signed-out visitor (see `RequireAuth`) and always at `/welcome`.
 * Every screenshot is the real product with the demo firm's sample data; no real client
 * appears. Claims here must stay true of what is live: uploads are not virus-scanned
 * until ClamAV is switched on (CURRENT_STATUS 8.8), so this page does not mention
 * scanning.
 *
 * IMAGES. Each screenshot ships twice: a display size chosen by `srcSet` (a phone never
 * downloads the large one) and the full capture, fetched only when somebody clicks to
 * enlarge it.
 */

import { cn, useFocusTrap, useScrollLock } from '@legalcrm/ui';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
  type RefObject,
} from 'react';
import { Link } from 'react-router-dom';

import mascot from '@/shell/assistant-lawyer.webp';

import appDocumentsFull from './img/app-documents-full.webp';
import appDocuments from './img/app-documents.webp';
import appHomeFull from './img/app-home-full.webp';
import appHome from './img/app-home.webp';
import appMatterFull from './img/app-matter-full.webp';
import appMatter from './img/app-matter.webp';
import appMattersFull from './img/app-matters-full.webp';
import appMatters from './img/app-matters.webp';
import appMessagesFull from './img/app-messages-full.webp';
import appMessages from './img/app-messages.webp';
import appPaymentsFull from './img/app-payments-full.webp';
import appPayments from './img/app-payments.webp';
import assistant1200 from './img/assistant-1200.webp';
import assistant2400 from './img/assistant-2400.webp';
import assistantFull from './img/assistant-full.webp';
import documents1200 from './img/documents-1200.webp';
import documents2400 from './img/documents-2400.webp';
import documentsFull from './img/documents-full.webp';
import invoices1200 from './img/invoices-1200.webp';
import invoices2400 from './img/invoices-2400.webp';
import invoicesFull from './img/invoices-full.webp';
import leads1200 from './img/leads-1200.webp';
import leads2400 from './img/leads-2400.webp';
import leadsFull from './img/leads-full.webp';
import matter1200 from './img/matter-1200.webp';
import matter2400 from './img/matter-2400.webp';
import matterFull from './img/matter-full.webp';
import myday1200 from './img/myday-1200.webp';
import myday2400 from './img/myday-2400.webp';
import mydayFull from './img/myday-full.webp';
import payments1200 from './img/payments-1200.webp';
import payments2400 from './img/payments-2400.webp';
import paymentsFull from './img/payments-full.webp';

const DEMO_PATH = '/demo';

// ---------------------------------------------------------------------------
// Click to enlarge
// ---------------------------------------------------------------------------

interface Enlarged {
  src: string;
  alt: string;
  phone?: boolean;
}

const EnlargeContext = createContext<(image: Enlarged) => void>(() => {});

function Lightbox({ image, onClose }: { image: Enlarged; onClose: () => void }) {
  const dialogRef = useRef<HTMLDivElement>(null);
  const closeRef = useRef<HTMLButtonElement>(null);
  useFocusTrap(dialogRef, true, { initialFocusRef: closeRef, onEscape: onClose });
  useScrollLock(true);

  return (
    <div
      ref={dialogRef}
      role="dialog"
      aria-modal="true"
      aria-label={image.alt}
      className="fixed inset-0 z-50 flex items-center justify-center bg-[#0b1020]/90 px-3 pb-3 pt-16 backdrop-blur-sm sm:px-8 sm:pb-8 sm:pt-20"
      onClick={onClose}
    >
      <button
        ref={closeRef}
        type="button"
        onClick={onClose}
        className="absolute right-3 top-3 rounded-full bg-white/10 px-4 py-2 text-sm font-medium text-white hover:bg-white/20 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white sm:right-6 sm:top-6"
      >
        Close <span aria-hidden="true">✕</span>
      </button>
      <img
        src={image.src}
        alt={image.alt}
        onClick={(event) => event.stopPropagation()}
        className={cn(
          'max-h-full max-w-full cursor-default object-contain shadow-2xl',
          image.phone ? 'rounded-[1.5rem]' : 'rounded-lg',
        )}
      />
    </div>
  );
}

/** A screenshot that opens full size. A button, so it works from the keyboard too. */
function Zoomable({
  full,
  alt,
  phone,
  className,
  children,
}: {
  full: string;
  alt: string;
  phone?: boolean;
  className?: string;
  children: ReactNode;
}) {
  const enlarge = useContext(EnlargeContext);
  return (
    <button
      type="button"
      onClick={() => enlarge({ src: full, alt, phone })}
      aria-label={`Enlarge: ${alt}`}
      className={cn(
        'group relative block w-full cursor-zoom-in text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-4',
        className,
      )}
    >
      {children}
      <span className="pointer-events-none absolute bottom-3 right-3 rounded-full bg-[#0b1020]/75 px-3 py-1 text-xs font-medium text-white opacity-0 transition group-hover:opacity-100 group-focus-visible:opacity-100">
        Click to enlarge
      </span>
    </button>
  );
}

// ---------------------------------------------------------------------------
// Frames
// ---------------------------------------------------------------------------

/**
 * The call to action floats: a slow rise and fall, so the eye finds it without it
 * shouting. Staggered per button so two on screen never bob in step. Paused on hover,
 * where it lifts instead, and still for anyone who asks for reduced motion.
 */
const CTA_STYLES = `
@keyframes cta-float {
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(-6px); }
}
@keyframes cta-shine {
  0% { transform: translateX(-120%) skewX(-20deg); }
  60%, 100% { transform: translateX(260%) skewX(-20deg); }
}
.cta-demo {
  animation: cta-float 2.8s ease-in-out infinite;
  background-image: linear-gradient(135deg, #ffd166 0%, #ff9f43 45%, #ff6b4a 100%);
  box-shadow: 0 10px 28px -6px rgba(255, 120, 60, 0.55), inset 0 1px 0 rgba(255, 255, 255, 0.45);
}
.cta-demo:hover { animation-play-state: paused; transform: translateY(-8px) scale(1.03); }
.cta-demo::after {
  content: ""; position: absolute; inset: 0 auto 0 0; width: 40%;
  background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.55), transparent);
  animation: cta-shine 3.6s ease-in-out infinite;
}
@media (prefers-reduced-motion: reduce) {
  .cta-demo, .cta-demo::after { animation: none; }
  .cta-demo:hover { transform: none; }
}
`;

function DemoButton({
  className,
  children = 'Start your free demo',
  delay = 0,
  tabIndex,
}: {
  className?: string;
  children?: ReactNode;
  /** Seconds to offset the float, so buttons on screen together move out of step. */
  delay?: number;
  tabIndex?: number;
}) {
  return (
    <Link
      to={DEMO_PATH}
      tabIndex={tabIndex}
      style={{ animationDelay: `${delay}s` }}
      className={cn(
        'cta-demo relative inline-flex items-center justify-center gap-2 overflow-hidden rounded-full px-6 py-3 text-sm font-bold tracking-wide text-[#1a1033] transition-transform duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-hero',
        className,
      )}
    >
      <span className="relative">{children}</span>
      <span aria-hidden="true" className="relative text-base">
        →
      </span>
    </Link>
  );
}

function BrowserFrame({
  src,
  srcSet,
  sizes,
  full,
  alt,
  eager,
  className,
}: {
  src: string;
  srcSet?: string;
  sizes?: string;
  full: string;
  alt: string;
  eager?: boolean;
  className?: string;
}) {
  return (
    <Zoomable full={full} alt={alt} className={className}>
      <div className="overflow-hidden rounded-xl border border-black/10 bg-white shadow-2xl shadow-black/20">
        <div className="flex items-center gap-2 border-b border-black/5 bg-[#efece4] px-3 py-2">
          <span className="h-2.5 w-2.5 rounded-full bg-black/15" />
          <span className="h-2.5 w-2.5 rounded-full bg-black/15" />
          <span className="h-2.5 w-2.5 rounded-full bg-black/15" />
          <span className="ml-2 truncate rounded-md bg-white px-3 py-0.5 text-[11px] text-black/50">
            legal-crm.wpcodie.com
          </span>
        </div>
        <img
          src={src}
          srcSet={srcSet}
          sizes={sizes}
          alt={alt}
          className="block w-full"
          loading={eager ? 'eager' : 'lazy'}
        />
      </div>
    </Zoomable>
  );
}

function PhoneFrame({
  src,
  full,
  alt,
  className,
  small = false,
}: {
  src: string;
  full: string;
  alt: string;
  className?: string;
  /**
   * Drawn small on a phone (the hero's). A full-size bezel and corner radius at a
   * quarter of the width ate into the screen and cut off the app's top bar and menu;
   * a real phone at that size shows a hairline bezel and gently rounded corners.
   */
  small?: boolean;
}) {
  return (
    <Zoomable full={full} alt={alt} phone className={className}>
      <div
        className={cn(
          'overflow-hidden border-[#141a2b] bg-[#141a2b] shadow-2xl shadow-black/30',
          small
            ? 'rounded-[0.8rem] border-[2px] sm:rounded-[2rem] sm:border-[6px]'
            : 'rounded-[2rem] border-[6px]',
        )}
      >
        <img
          src={src}
          srcSet={`${src} 600w, ${full} 1170w`}
          sizes="(min-width: 1024px) 300px, 45vw"
          alt={alt}
          className={cn(
            'block w-full',
            small ? 'rounded-[0.65rem] sm:rounded-[1.55rem]' : 'rounded-[1.55rem]',
          )}
          loading="lazy"
        />
      </div>
    </Zoomable>
  );
}

// ---------------------------------------------------------------------------
// Content
// ---------------------------------------------------------------------------

const FEATURES = [
  {
    eyebrow: 'My day',
    title: 'Every morning starts with what needs you.',
    body: 'Deadlines at risk, documents to review and payments to verify, in the order they need you. Each person sees their own work, so a paralegal’s day is a paralegal’s day.',
    points: [
      'Critical dates escalate before they are missed',
      'Overdue tasks surface on their own',
      'One click from a number to the work behind it',
    ],
    images: [
      {
        small: myday1200,
        large: myday2400,
        full: mydayFull,
        alt: 'The My day dashboard: critical dates at risk, documents to review, payments to verify and open tasks.',
      },
    ],
  },
  {
    eyebrow: 'Matters',
    title: 'The whole matter on one page.',
    body: 'Stage, team, critical dates, open tasks, what the client still owes you and where the money stands. Everything a fee earner needs before a call, without opening five tabs.',
    points: [
      'Workflows per matter type, with guarded stage changes',
      'Documents reviewed, approved or sent back with a reason',
      'A timeline of everything that happened',
    ],
    images: [
      {
        small: matter1200,
        large: matter2400,
        full: matterFull,
        alt: 'A matter page showing its summary, team, outstanding client documents, critical dates and financial position.',
      },
    ],
  },
  {
    eyebrow: 'Leads',
    title: 'From first enquiry to signed client.',
    body: 'Track every enquiry from the website form or a CSV import, see who owns it and how quickly they answered, then send the engagement letter and open the matter without retyping anything.',
    points: [
      'Conflict check before you take a client on',
      'Engagement letters signed from a secure link',
      'Converted leads keep their full history',
    ],
    images: [
      {
        small: leads1200,
        large: leads2400,
        full: leadsFull,
        alt: 'The leads list with references, enquirers, stages, sources, owners and first-response times.',
      },
    ],
  },
  {
    eyebrow: 'Documents',
    title: 'Every document, and where it stands.',
    body: 'Everything clients send, across every matter you can see: who sent it, whether it has been reviewed, and whether the client can see it. A re-sent file comes back to the top with its new date.',
    points: [
      'Approve, or send back with a reason the client can act on',
      'Internal documents are marked, never shown to the client',
      'A review queue of everything waiting on somebody',
    ],
    images: [
      {
        small: documents1200,
        large: documents2400,
        full: documentsFull,
        alt: 'The documents library: titles, clients, review status and visibility for every file.',
      },
    ],
  },
  {
    eyebrow: 'Invoices & payments',
    title: 'Know what is billed, paid and still owed.',
    body: 'Invoices per matter with what is outstanding and when it is due. When a client uploads proof of payment from the app, it waits here until somebody checks it against the invoice.',
    points: [
      'Outstanding and overdue at a glance',
      'Client proof of payment verified by the firm, never assumed',
      'Every payment linked to its invoice and matter',
    ],
    images: [
      {
        small: invoices1200,
        large: invoices2400,
        full: invoicesFull,
        alt: 'The invoices list with totals, amounts paid, outstanding balances and due dates.',
      },
      {
        small: payments1200,
        large: payments2400,
        full: paymentsFull,
        alt: 'The payments queue: proof of payment from a client waiting to be verified.',
      },
    ],
  },
] as const;

/** Everything in the staff app, grouped the way its own navigation groups it. */
const CRM_FUNCTIONS = [
  {
    group: 'Daily work',
    items: [
      ['My day', 'Your deadlines, reviews, payments and tasks, most urgent first.'],
      ['Matters', 'Stage, team, critical dates, documents, money and timeline.'],
      ['Tasks', 'Assigned, due and overdue work, flagged automatically.'],
      ['Critical dates', 'Filing and limitation dates, with reminders as they approach.'],
      ['Calendar', 'Hearings, appointments and deadlines in one view.'],
    ],
  },
  {
    group: 'Winning clients',
    items: [
      ['Leads', 'Every enquiry, its owner, stage and first-response time.'],
      ['Intake forms & CSV import', 'Enquiries arrive from your website or a spreadsheet.'],
      ['Conflict check', 'Checked against existing clients before you act.'],
      ['Clients', 'People and companies, their contacts and portal access.'],
    ],
  },
  {
    group: 'Engagement & documents',
    items: [
      ['Engagement letters', 'Drafted from your templates and signed from a secure link.'],
      ['Document requests', 'Ask a client for what you need, with a due date.'],
      ['Review queue', 'Approve a document, or send it back with a reason the client can act on.'],
      ['Document library', 'Every file by matter and client, internal or shared.'],
    ],
  },
  {
    group: 'Money',
    items: [
      ['Invoices', 'Issued, part paid and overdue, per matter.'],
      ['Payment verification', 'A client’s proof of payment waits for your confirmation.'],
      ['Reports', 'Workload, pipeline and finance at a glance.'],
    ],
  },
  {
    group: 'Talking to clients',
    items: [
      ['Conversations', 'Message threads with clients, about a matter.'],
      ['Client updates', 'Plain-words news you publish to the client app.'],
      ['Notifications & email', 'Alerts in the app and from your firm’s own email address.'],
    ],
  },
  {
    group: 'Running the firm',
    items: [
      ['Users & roles', 'Administrator, partner, lawyer, paralegal, finance and more.'],
      ['Departments & offices', 'Work organised the way your firm is.'],
      ['Matter types & workflows', 'Your services, their stages and the documents each needs.'],
      ['Custom fields', 'The extra details your practice records.'],
      ['AI assistant', 'Answers questions from your records, within your permissions.'],
    ],
  },
] as const;

const APP_SCREENS = [
  {
    title: 'Home',
    body: 'What the firm needs from them today, with the most urgent thing first.',
    src: appHome,
    full: appHomeFull,
  },
  {
    title: 'Your matters',
    body: 'Every matter with the firm, and where each one stands in plain words.',
    src: appMatters,
    full: appMattersFull,
  },
  {
    title: 'One matter',
    body: 'What is happening, what is still needed, and updates written by the team.',
    src: appMatter,
    full: appMatterFull,
  },
  {
    title: 'Documents',
    body: 'Send a file or a photo from the phone. A copy that needs redoing comes back with the reason.',
    src: appDocuments,
    full: appDocumentsFull,
  },
  {
    title: 'Messages',
    body: 'Conversations with the firm, kept with the matter they are about.',
    src: appMessages,
    full: appMessagesFull,
  },
  {
    title: 'Payments',
    body: 'What is owed and what is paid. Upload proof of payment and the firm confirms it.',
    src: appPayments,
    full: appPaymentsFull,
  },
] as const;

const APP_EXTRAS = [
  ['Sign engagement letters', 'Read the letter and accept it in the app.'],
  ['Push notifications', 'A nudge when the firm needs something or replies.'],
  ['Face ID & fingerprint', 'Unlock with biometrics, with a password fallback.'],
  ['Your details', 'Clients keep their own contact details up to date.'],
  ['iPhone, Android & web', 'One account across the phone and the browser.'],
  ['Only their own file', 'Each client sees their matters and nothing else.'],
] as const;

const BRIEFING = [
  { label: 'Critical dates at risk', count: 2, tone: 'bg-red-700' },
  { label: 'Documents to review', count: 4, tone: 'bg-amber-700' },
  { label: 'Payments to verify', count: 1, tone: 'bg-amber-700' },
  { label: 'My open tasks', count: 0, tone: 'bg-slate-500' },
] as const;

const TRUST = [
  {
    title: 'Each firm is walled off',
    body: 'Every row of data belongs to one firm, and the database itself refuses to show it to anybody else, not only the screens.',
  },
  {
    title: 'Internal notes stay internal',
    body: 'Staff reasoning, conflict checks and case history never reach a client. They are left out of what the client app receives, not hidden after the fact.',
  },
  {
    title: 'People see their own work',
    body: 'Roles from administrator to paralegal to external counsel decide what each person can open and change.',
  },
  {
    title: 'A record of who did what',
    body: 'Changes are audited, and a guard that stops an action says why, so somebody can put it right.',
  },
] as const;

function Eyebrow({ children, onDark }: { children: ReactNode; onDark?: boolean }) {
  return (
    <p
      className={cn(
        'text-xs font-semibold uppercase tracking-[0.25em]',
        onDark ? 'text-accent' : 'text-accent-ink',
      )}
    >
      {children}
    </p>
  );
}

function Dot() {
  return <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-accent" aria-hidden="true" />;
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

/**
 * True while the element is on screen. Starts true, so the sticky bar does not flash
 * in during the first render, and stays true where IntersectionObserver is missing.
 */
function useInView<T extends HTMLElement>(): [RefObject<T>, boolean] {
  const ref = useRef<T>(null);
  const [visible, setVisible] = useState(true);
  useEffect(() => {
    const node = ref.current?.firstElementChild ?? ref.current;
    if (!node || typeof IntersectionObserver === 'undefined') return;
    const observer = new IntersectionObserver(
      ([entry]) => setVisible(entry?.isIntersecting ?? true),
      {
        threshold: 0,
      },
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, []);
  return [ref, visible];
}

/**
 * The demo button comes back once the hero's has scrolled away: always one tap away
 * while reading, never shown twice at once.
 */
function StickyBar({ visible }: { visible: boolean }) {
  return (
    <div
      aria-hidden={!visible}
      className={cn(
        'fixed inset-x-0 top-0 z-40 border-b sm:hidden border-hero-line bg-hero/90 pt-[env(safe-area-inset-top,0px)] text-hero-ink shadow-lg backdrop-blur-md transition-transform duration-300 motion-reduce:transition-none',
        visible ? 'translate-y-0' : 'pointer-events-none -translate-y-full',
      )}
    >
      <div className="mx-auto flex max-w-6xl items-center justify-between gap-4 px-4 py-2.5 sm:px-6">
        <a href="#top" className="flex items-center gap-2" tabIndex={visible ? 0 : -1}>
          <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-hero-ink font-display text-base font-semibold text-hero">
            W
          </span>
          <span className="text-sm font-semibold">Legal CRM</span>
        </a>
        <div className="flex items-center gap-4">
          <Link
            to="/login"
            tabIndex={visible ? 0 : -1}
            className="hidden text-sm font-medium text-hero-muted hover:text-hero-ink sm:inline"
          >
            Sign in
          </Link>
          <DemoButton className="px-4 py-2" tabIndex={visible ? 0 : -1}>
            Free demo
          </DemoButton>
        </div>
      </div>
    </div>
  );
}

export function LandingPage() {
  const [enlarged, setEnlarged] = useState<Enlarged | null>(null);
  const close = useCallback(() => setEnlarged(null), []);
  const [heroCtaRef, heroCtaVisible] = useInView<HTMLSpanElement>();

  return (
    <EnlargeContext.Provider value={setEnlarged}>
      <div className="min-h-screen bg-canvas font-sans text-ink">
        <style>{CTA_STYLES}</style>
        {/* -- Header ---------------------------------------------------- */}
        <header className="absolute inset-x-0 top-0 z-10">
          <nav
            className="mx-auto flex max-w-6xl items-center justify-between gap-4 px-4 py-5 sm:px-6"
            aria-label="Main"
          >
            <a href="#top" className="flex items-center gap-2.5 text-hero-ink">
              <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-hero-ink font-display text-lg font-semibold text-hero">
                W
              </span>
              <span className="leading-tight">
                <span className="block text-sm font-semibold">WPCodie</span>
                <span className="block text-xs text-hero-muted">Legal CRM</span>
              </span>
            </a>
            <div className="hidden items-center gap-7 text-sm text-hero-muted md:flex">
              <a href="#features" className="hover:text-hero-ink">
                Features
              </a>
              <a href="#functions" className="hover:text-hero-ink">
                Everything inside
              </a>
              <a href="#client-app" className="hover:text-hero-ink">
                Client app
              </a>
              <a href="#assistant" className="hover:text-hero-ink">
                AI assistant
              </a>
              <a href="#trust" className="hover:text-hero-ink">
                Security
              </a>
            </div>
            {/* On a phone: one demo button per screen (the hero's), and "Sign in" here --
                otherwise somebody with an account had no way in from a phone. Wider
                screens keep both. */}
            <Link
              to="/login"
              className="rounded-full border border-hero-line px-4 py-2 text-sm font-semibold text-hero-ink transition hover:bg-white/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white sm:hidden"
            >
              Sign in
            </Link>
            <div className="hidden items-center gap-3 sm:flex">
              <Link to="/login" className="text-sm font-medium text-hero-ink hover:underline">
                Sign in
              </Link>
              <DemoButton className="px-4 py-2" delay={0.9}>
                Free demo
              </DemoButton>
            </div>
          </nav>
        </header>

        <StickyBar visible={!heroCtaVisible} />

        <main id="top">
          {/* -- Hero ---------------------------------------------------- */}
          {/* On a phone the first screen is one complete thought: headline, one line,
              the button, and the whole product picture -- nothing cut by the fold. The
              chips and the longer sentence are for wider screens only. */}
          <section className="relative overflow-hidden bg-hero pb-12 pt-24 text-hero-ink max-[380px]:pt-20 sm:pb-24 sm:pt-36">
            <div
              aria-hidden="true"
              className="pointer-events-none absolute -right-40 -top-40 h-[36rem] w-[36rem] rounded-full bg-[radial-gradient(closest-side,rgba(96,140,220,0.35),transparent)]"
            />
            {/* Three blocks. A phone reads them headline, picture, button -- the picture
                is the proof, the button the next step. Tablets keep headline, button,
                picture; desktop puts the picture in its own column beside both. */}
            <div className="relative mx-auto grid max-w-6xl items-center gap-y-7 px-4 sm:gap-y-8 sm:px-6 lg:grid-cols-[1fr_1.15fr] lg:gap-x-12 lg:gap-y-0">
              <div className="order-1 lg:col-start-1 lg:row-start-1 lg:self-end">
                <Eyebrow onDark>Case management for law firms</Eyebrow>
                <h1 className="mt-3 font-display text-[2.35rem] leading-[1.05] max-[380px]:text-[2.05rem] sm:mt-4 sm:text-6xl">
                  Run your law firm from one calm screen.
                </h1>
                <p className="mt-4 max-w-xl text-base leading-relaxed text-hero-muted sm:mt-6 sm:text-lg">
                  <span className="sm:hidden">
                    Matters, clients, documents and deadlines in one place, with an app for your
                    clients.
                  </span>
                  <span className="hidden sm:inline">
                    Matters, clients, documents, deadlines and payments in one place, plus a client
                    app that keeps every client informed without the phone calls.
                  </span>
                </p>
              </div>

              <div className="order-3 sm:order-2 lg:col-start-1 lg:row-start-2 lg:self-start lg:pt-8">
                <div className="flex flex-col items-stretch gap-3 sm:flex-row sm:flex-wrap sm:items-center sm:gap-4">
                  <span ref={heroCtaRef} className="contents">
                    <DemoButton className="w-full px-7 py-4 text-base sm:w-auto" />
                  </span>
                  <a
                    href="#features"
                    className="text-center text-sm font-medium text-hero-ink hover:underline sm:text-left"
                  >
                    See what it does
                  </a>
                </div>
                <ul className="mt-10 hidden flex-wrap gap-2.5 text-sm sm:flex">
                  {['Case management', 'Client app', 'AI assistant'].map((label) => (
                    <li
                      key={label}
                      className="flex items-center gap-2 rounded-full border border-hero-line px-3.5 py-1.5"
                    >
                      <span className="h-1.5 w-1.5 rounded-full bg-accent" aria-hidden="true" />
                      {label}
                    </li>
                  ))}
                </ul>
              </div>

              <div className="relative order-2 sm:order-3 sm:pb-16 lg:col-start-2 lg:row-span-2 lg:row-start-1 lg:pb-0">
                <BrowserFrame
                  src={myday1200}
                  srcSet={`${myday1200} 1200w, ${myday2400} 2400w`}
                  sizes="(min-width: 1024px) 760px, 100vw"
                  full={mydayFull}
                  alt="Legal CRM's My day dashboard for a sample firm."
                  eager
                  className="lg:-mr-24 lg:w-[calc(100%+6rem)]"
                />
                <PhoneFrame
                  src={appHome}
                  full={appHomeFull}
                  alt="The client app's home screen telling a client what the firm needs from them."
                  small
                  className="absolute bottom-2 right-2 w-[24%] max-w-[11rem] sm:-bottom-6 sm:left-[-1.5rem] sm:right-auto sm:w-[30%]"
                />
              </div>
            </div>
          </section>

          {/* -- Features ------------------------------------------------ */}
          <section id="features" className="scroll-mt-8 py-20 sm:py-28">
            <div className="mx-auto max-w-6xl space-y-24 px-4 sm:px-6">
              {FEATURES.map((feature, index) => (
                <article
                  key={feature.eyebrow}
                  className="grid items-center gap-10 lg:grid-cols-2 lg:gap-14"
                >
                  <div className={cn(index % 2 === 1 && 'lg:order-2')}>
                    <Eyebrow>{feature.eyebrow}</Eyebrow>
                    <h2 className="mt-3 font-display text-3xl leading-tight sm:text-4xl">
                      {feature.title}
                    </h2>
                    <p className="mt-4 text-base leading-relaxed text-ink-muted">{feature.body}</p>
                    <ul className="mt-6 space-y-2.5 text-sm">
                      {feature.points.map((point) => (
                        <li key={point} className="flex gap-3">
                          <Dot />
                          {point}
                        </li>
                      ))}
                    </ul>
                  </div>
                  <div className="grid gap-6">
                    {feature.images.map((image) => (
                      <BrowserFrame
                        key={image.full}
                        src={image.small}
                        srcSet={`${image.small} 1200w, ${image.large} 2400w`}
                        sizes="(min-width: 1024px) 540px, 100vw"
                        full={image.full}
                        alt={image.alt}
                      />
                    ))}
                  </div>
                </article>
              ))}
            </div>
          </section>

          {/* -- Everything inside -------------------------------------- */}
          <section
            id="functions"
            className="scroll-mt-8 border-y border-line bg-surface py-20 sm:py-28"
          >
            <div className="mx-auto max-w-6xl px-4 sm:px-6">
              <div className="max-w-2xl">
                <Eyebrow>Everything inside Legal CRM</Eyebrow>
                <h2 className="mt-3 font-display text-3xl leading-tight sm:text-4xl">
                  One system for the whole life of a matter.
                </h2>
                <p className="mt-4 text-base leading-relaxed text-ink-muted">
                  From the first enquiry to the final invoice, every part of the work has its place,
                  and every person sees the parts that are theirs.
                </p>
              </div>
              <div className="mt-12 grid gap-x-10 gap-y-12 sm:grid-cols-2 lg:grid-cols-3">
                {CRM_FUNCTIONS.map((section) => (
                  <div key={section.group}>
                    <h3 className="border-b border-line pb-3 text-sm font-semibold uppercase tracking-[0.12em] text-accent-ink">
                      {section.group}
                    </h3>
                    <dl className="mt-4 space-y-4">
                      {section.items.map(([name, description]) => (
                        <div key={name}>
                          <dt className="font-semibold">{name}</dt>
                          <dd className="mt-0.5 text-sm leading-relaxed text-ink-muted">
                            {description}
                          </dd>
                        </div>
                      ))}
                    </dl>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* -- Client app ---------------------------------------------- */}
          <section id="client-app" className="scroll-mt-8 bg-hero py-20 text-hero-ink sm:py-28">
            <div className="mx-auto max-w-6xl px-4 sm:px-6">
              <div className="max-w-2xl">
                <Eyebrow onDark>The client app · iPhone, Android and web</Eyebrow>
                <h2 className="mt-3 font-display text-3xl leading-tight sm:text-5xl">
                  &ldquo;Any update on my case?&rdquo; Answered before it&rsquo;s asked.
                </h2>
                <p className="mt-5 text-lg leading-relaxed text-hero-muted">
                  Clients see what the firm still needs, where their matter stands in plain words,
                  what they owe, and messages from the team. Fewer chasing calls for you, more
                  confidence for them.
                </p>
              </div>

              <div className="mt-14 grid grid-cols-2 gap-x-5 gap-y-12 sm:gap-x-8 lg:grid-cols-3 lg:gap-x-14">
                {APP_SCREENS.map((screen) => (
                  <figure key={screen.title} className="mx-auto w-full max-w-[18rem]">
                    <PhoneFrame
                      src={screen.src}
                      full={screen.full}
                      alt={`Client app: ${screen.title}. ${screen.body}`}
                    />
                    <figcaption className="mt-5">
                      <span className="block font-semibold">{screen.title}</span>
                      <span className="mt-1 block text-sm leading-relaxed text-hero-muted">
                        {screen.body}
                      </span>
                    </figcaption>
                  </figure>
                ))}
              </div>

              <div className="mt-20 border-t border-hero-line pt-12">
                <h3 className="font-display text-2xl">Also in the app</h3>
                <dl className="mt-8 grid gap-x-10 gap-y-6 sm:grid-cols-2 lg:grid-cols-3">
                  {APP_EXTRAS.map(([name, description]) => (
                    <div key={name} className="flex gap-3">
                      <Dot />
                      <div>
                        <dt className="font-semibold">{name}</dt>
                        <dd className="mt-0.5 text-sm text-hero-muted">{description}</dd>
                      </div>
                    </div>
                  ))}
                </dl>
              </div>
            </div>
          </section>

          {/* -- Assistant ----------------------------------------------- */}
          <section id="assistant" className="scroll-mt-8 py-20 sm:py-28">
            <div className="mx-auto max-w-6xl px-4 sm:px-6">
              <div className="grid items-center gap-10 lg:grid-cols-2 lg:gap-14">
                <div>
                  <div>
                    <Eyebrow>AI assistant</Eyebrow>
                    <h2 className="mt-3 font-display text-3xl leading-tight sm:text-4xl">
                      Ask your CRM. Get an answer from your own records.
                    </h2>
                    <p className="mt-4 text-base leading-relaxed text-ink-muted">
                      &ldquo;What needs my attention today?&rdquo; &ldquo;Which invoices are
                      overdue?&rdquo; &ldquo;Summarise this matter.&rdquo; The assistant only reads,
                      and it sees exactly what you are allowed to see, never more.
                    </p>
                    <p className="mt-4 text-base leading-relaxed text-ink-muted">
                      And each morning it walks in with a short briefing of your day.
                    </p>
                  </div>
                  <div className="mt-8 flex items-end gap-2">
                    <div className="w-60 rounded-xl border border-line bg-white p-4 text-[#1b2333] shadow-xl sm:w-64">
                      <div className="flex items-baseline justify-between text-[11px] uppercase tracking-[0.15em] text-black/50">
                        <span>From your day</span>
                        <span className="normal-case tracking-normal">This morning</span>
                      </div>
                      <ul className="mt-3 space-y-2.5 text-sm">
                        {BRIEFING.map((row) => (
                          <li key={row.label} className="flex items-center gap-2.5">
                            <span
                              className={cn('h-2 w-2 rounded-full', row.tone)}
                              aria-hidden="true"
                            />
                            <span className="flex-1">{row.label}</span>
                            <span className="font-semibold tabular-nums">{row.count}</span>
                          </li>
                        ))}
                      </ul>
                      <p className="mt-3 flex justify-between border-t border-black/10 pt-2.5 text-sm text-[#1f3a6b]">
                        Ask something else <span aria-hidden="true">→</span>
                      </p>
                    </div>
                    <img
                      src={mascot}
                      alt="The assistant character, in a black suit with a briefcase."
                      className="w-20 shrink-0 drop-shadow-xl sm:w-28"
                      loading="lazy"
                    />
                  </div>
                </div>
                <BrowserFrame
                  src={assistant1200}
                  srcSet={`${assistant1200} 1200w, ${assistant2400} 2400w`}
                  sizes="(min-width: 1024px) 540px, 100vw"
                  full={assistantFull}
                  alt="The assistant answering 'What needs my attention today?' from the firm's own records."
                />
              </div>
            </div>
          </section>

          {/* -- Trust --------------------------------------------------- */}
          <section
            id="trust"
            className="scroll-mt-8 border-y border-line bg-surface py-20 sm:py-24"
          >
            <div className="mx-auto max-w-6xl px-4 sm:px-6">
              <div className="max-w-2xl">
                <Eyebrow>Built for confidentiality</Eyebrow>
                <h2 className="mt-3 font-display text-3xl leading-tight sm:text-4xl">
                  One firm can never see another&rsquo;s data.
                </h2>
              </div>
              <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
                {TRUST.map((item) => (
                  <div key={item.title} className="rounded-xl border border-line bg-canvas p-6">
                    <h3 className="font-semibold">{item.title}</h3>
                    <p className="mt-2 text-sm leading-relaxed text-ink-muted">{item.body}</p>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* -- Closing call to action --------------------------------- */}
          <section className="bg-hero py-20 text-hero-ink sm:py-24">
            <div className="mx-auto flex max-w-4xl flex-col items-center px-4 text-center sm:px-6">
              <h2 className="font-display text-3xl leading-tight sm:text-5xl">
                See it with a sample firm.
              </h2>
              <p className="mt-5 max-w-xl text-lg text-hero-muted">
                Matters, leads, documents and the assistant, with demo data. Nothing to install.
              </p>
              <DemoButton className="mt-8 px-8 py-4 text-base" delay={0.4} />
            </div>
          </section>
        </main>

        <footer className="bg-hero pb-10 text-hero-muted">
          <div className="mx-auto flex max-w-6xl flex-col items-center justify-between gap-4 border-t border-hero-line px-4 pt-8 text-sm sm:flex-row sm:px-6">
            <p>&copy; {new Date().getFullYear()} WPCodie · Legal CRM</p>
            <div className="flex gap-6">
              <Link to="/login" className="hover:text-hero-ink">
                Staff sign in
              </Link>
              <a href="https://wpcodie.com" className="hover:text-hero-ink">
                wpcodie.com
              </a>
            </div>
          </div>
        </footer>

        {enlarged && <Lightbox image={enlarged} onClose={close} />}
      </div>
    </EnlargeContext.Provider>
  );
}
